// Recording interface — the UI is complete and clearly marks the feature
// as experimental in the Beta. Actual capture uses browser
// getDisplayMedia when available, otherwise the buttons remain visible
// but a banner explains the limitation.
import { useEffect, useRef, useState } from "react";
import LobbyLayout from "@/components/LobbyLayout";
import { SCREEN } from "@/stores/gameStore";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Video, Play, Trash2, Download, Circle, Square, Edit } from "lucide-react";

export default function RecordingsScreen() {
    const t = useT();
    const [recording, setRecording] = useState(false);
    const [items, setItems] = useState(() => loadRecordings());
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);

    // Persist metadata (not the blobs — those are only kept in-memory)
    useEffect(() => {
        try {
            const clean = items.map(({ id, name, createdAt, sizeKb, durationSec }) => ({
                id, name, createdAt, sizeKb, durationSec,
            }));
            localStorage.setItem("axis_recordings_meta", JSON.stringify(clean));
        } catch (e) {}
    }, [items]);

    const supported = typeof navigator !== "undefined" && !!(navigator.mediaDevices?.getDisplayMedia);

    const start = async () => {
        if (!supported) {
            toast.error("Screen capture is not supported in this browser.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: { frameRate: 30 },
                audio: true,
            });
            streamRef.current = stream;
            const recorder = new MediaRecorder(stream, { mimeType: "video/webm" });
            chunksRef.current = [];
            recorder.ondataavailable = (e) => {
                if (e.data.size) chunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: "video/webm" });
                const url = URL.createObjectURL(blob);
                const durationSec = Math.max(1, Math.round((Date.now() - startTs) / 1000));
                const it = {
                    id: Math.random().toString(36).slice(2, 8),
                    name: `Clip ${new Date().toLocaleString()}`,
                    createdAt: Date.now(),
                    sizeKb: Math.round(blob.size / 1024),
                    durationSec,
                    url,
                };
                setItems((prev) => [it, ...prev]);
                streamRef.current?.getTracks().forEach((tr) => tr.stop());
                streamRef.current = null;
            };
            const startTs = Date.now();
            recorder.start();
            mediaRecorderRef.current = recorder;
            setRecording(true);
            toast.success("Recording started");
        } catch (e) {
            toast.error("Recording cancelled or blocked");
        }
    };

    const stop = () => {
        const rec = mediaRecorderRef.current;
        if (rec && rec.state !== "inactive") {
            rec.stop();
        }
        setRecording(false);
    };

    const play = (it) => {
        if (!it.url) {
            toast.error("This clip's video data was cleared when you reloaded.");
            return;
        }
        window.open(it.url, "_blank");
    };
    const download = (it) => {
        if (!it.url) {
            toast.error("Download unavailable — reload cleared the buffer.");
            return;
        }
        const a = document.createElement("a");
        a.href = it.url;
        a.download = `${it.name.replace(/\s+/g, "_")}.webm`;
        a.click();
    };
    const rename = (it) => {
        const next = prompt("New name", it.name);
        if (next) setItems((prev) => prev.map((x) => x.id === it.id ? { ...x, name: next } : x));
    };
    const del = (it) => setItems((prev) => prev.filter((x) => x.id !== it.id));

    return (
        <LobbyLayout active={SCREEN.RECORDINGS}>
            <div data-testid="recordings-screen" className="w-full h-full overflow-hidden px-8 py-6 flex flex-col">
                <div className="mb-4">
                    <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">
                        {t("recordings.subtitle")}
                    </div>
                    <div className="axis-heading text-3xl tracking-[0.4em] text-white flex items-center gap-3">
                        <Video size={22} className="text-orange-400" />
                        {t("recordings.title")}
                    </div>
                </div>

                {/* Experimental banner */}
                <div className="axis-cut-sm border border-orange-400/40 bg-orange-500/10 px-4 py-2.5 mb-4 flex items-center gap-3">
                    <span className="axis-mono text-[10px] tracking-[0.4em] text-orange-300 px-2 py-0.5 border border-orange-400/60 axis-cut-sm">
                        EXPERIMENTAL
                    </span>
                    <span className="axis-mono text-xs text-white/70">
                        {t("recordings.experimental")}
                    </span>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-3 mb-6">
                    {!recording ? (
                        <button
                            data-testid="recording-start"
                            onClick={start}
                            className="axis-btn axis-cut-sm px-6 py-3 border border-red-500 bg-red-500/20 text-red-200 hover:bg-red-500/40 hover:text-white transition flex items-center gap-2"
                        >
                            <Circle size={14} className="fill-current" />
                            <span className="axis-heading tracking-widest text-sm">{t("recordings.start")}</span>
                        </button>
                    ) : (
                        <button
                            data-testid="recording-stop"
                            onClick={stop}
                            className="axis-btn axis-cut-sm px-6 py-3 border border-white bg-white/15 text-white hover:bg-white/25 transition flex items-center gap-2"
                        >
                            <Square size={14} className="fill-current" />
                            <span className="axis-heading tracking-widest text-sm">{t("recordings.stop")}</span>
                        </button>
                    )}
                    {recording && (
                        <div className="flex items-center gap-2 axis-mono text-[11px] tracking-[0.4em] text-red-400 animate-pulse">
                            <Circle size={10} className="fill-current" />
                            {t("recordings.indicator")}
                        </div>
                    )}
                    {!supported && (
                        <div className="axis-mono text-[11px] text-white/50">
                            Screen capture API unavailable in this browser
                        </div>
                    )}
                </div>

                {/* Clip list */}
                <div className="axis-cut-sm border border-white/10 bg-black/30 flex-1 overflow-y-auto p-4">
                    <div className="axis-mono text-[10px] tracking-[0.4em] text-white/50 mb-3">
                        {t("recordings.mine")} ({items.length})
                    </div>
                    {items.length === 0 ? (
                        <div className="h-full flex items-center justify-center axis-mono text-[11px] tracking-[0.3em] text-white/40">
                            No recordings yet. Start one above.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {items.map((it) => (
                                <div
                                    key={it.id}
                                    className="axis-cut-sm border border-white/10 px-4 py-3 flex items-center gap-3"
                                >
                                    <Video size={16} className="text-orange-300 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="axis-heading text-sm text-white tracking-wider truncate">
                                            {it.name}
                                        </div>
                                        <div className="axis-mono text-[10px] tracking-[0.3em] text-white/40 mt-0.5">
                                            {new Date(it.createdAt).toLocaleString()} · {it.durationSec}s · {it.sizeKb}kb
                                        </div>
                                    </div>
                                    <IconBtn onClick={() => play(it)} icon={Play} label={t("recordings.play")} />
                                    <IconBtn onClick={() => rename(it)} icon={Edit} label={t("recordings.rename")} />
                                    <IconBtn onClick={() => download(it)} icon={Download} label={t("recordings.download")} />
                                    <IconBtn onClick={() => del(it)} icon={Trash2} label={t("recordings.delete")} danger />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </LobbyLayout>
    );
}

function IconBtn({ icon: Icon, onClick, label, danger }) {
    return (
        <button
            onClick={onClick}
            title={label}
            className={`axis-cut-sm p-2 border border-white/10 hover:border-white/40 transition ${
                danger ? "hover:!border-red-500/70 hover:text-red-400" : ""
            }`}
        >
            <Icon size={12} />
        </button>
    );
}

function loadRecordings() {
    try {
        const raw = localStorage.getItem("axis_recordings_meta");
        return raw ? JSON.parse(raw) : [];
    } catch (e) {
        return [];
    }
}
